import _forOwn from 'lodash-es/forOwn';
import _groupBy from 'lodash-es/groupBy';
import _isEqual from 'lodash-es/isEqual';

import {
  asFieldValues,
  asMetadataValuesResponse,
  type MetadataFieldValuesPatched,
  type MetadataMetadataValuesSchemaPatched,
  type MetadataValues,
} from '@iconik-io/iconik-api/src/schema_patched/metadata';
import {
  httpGetAssetsByAssetIdByObjectTypeByObjectIdViewsByViewId,
  httpGetAssetsByAssetIdVersionsByVersionIdViewsByViewId,
  httpGetByObjectTypeByObjectIdViewsByViewId,
  httpPutAssetsByAssetIdByObjectTypeByObjectIdViewsByViewId,
  httpPutByObjectTypeByObjectIdViewsByViewId,
  httpPutByObjectTypeViewsByViewId,
} from '@iconik-io/iconik-api/src/services/metadata';
import {
  isSearchObjectType,
  OBJECT_TYPE_ASSETS,
  type ObjectType,
  type SearchObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type {
  SearchItemEntity,
  SearchItemEntityNormalized,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import type { AppDispatch, GetState } from '../../store/types';
import {
  formToServerFormat,
  pickBulkMarkedFields,
  serverToReduxFormat,
} from '../../utils/format_metadata_values';
import { cancellable } from '../http/actions';
import { successMessage } from '../messages/thunks';
import type { CategoryObjectType } from '../pages/admin_metadata_categories/types';
import { selectBlockFieldNames, selectWarningFieldNames } from './selectors';
import {
  metadataValuesCreateAwaiting,
  metadataValuesGetAsset,
  metadataValuesPutAsset,
} from './slice';

export const SEGMENT_CATEGORY_METADATA_FIELD = '__segment_category';
export const ASSET_CATEGORY_METADATA_FIELD = '__asset_category';
export const COLLECTION_CATEGORY_METADATA_FIELD = '__collection_category';

export function getMetadataValuesForObject(
  assetId: string,
  objectType: ObjectType,
  objectId: string,
  viewId: string,
) {
  return (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(
      httpGetAssetsByAssetIdByObjectTypeByObjectIdViewsByViewId(
        assetId,
        objectType,
        objectId,
        viewId,
      ),
    ).then((response) =>
      dispatch(metadataValuesGetAsset(asMetadataValuesResponse(response.body))),
    );
}

type GetMetadataValuesOptions = {
  viewId: string;
  objectType?: CategoryObjectType;
  versionId?: string;
};
export const getMetadataValues =
  (objectId: string, options: GetMetadataValuesOptions) =>
  (dispatch: AppDispatch): Promise<MetadataMetadataValuesSchemaPatched> => {
    const optionsMerged = {
      ...options,
      objectType: options?.objectType ?? 'assets',
    };

    const { viewId, objectType, versionId } = optionsMerged;

    const request = versionId
      ? httpGetAssetsByAssetIdVersionsByVersionIdViewsByViewId(
          objectId,
          versionId,
          viewId,
        )
      : httpGetByObjectTypeByObjectIdViewsByViewId(
          objectType,
          objectId,
          viewId,
        );

    return dispatch(
      cancellable(request, `${viewId}_${objectId}_${versionId}_${objectType}`),
    ).then((response) => {
      const data = asMetadataValuesResponse(response.body);
      dispatch(metadataValuesGetAsset(data));
      return data;
    });
  };

const updateBlockWarningFields =
  (
    viewId: string,
    data: MetadataValues,
    objectIds: string[],
    objectType: SearchObjectType = OBJECT_TYPE_ASSETS,
  ) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const update: Partial<SearchItemEntityNormalized> = {};
    const warningFields = selectWarningFieldNames(state, viewId);
    warningFields.some((fieldName) => {
      if (fieldName in data) {
        const value = data[fieldName];
        if (Array.isArray(value)) {
          const first = value[0];
          update.warning =
            (typeof first === 'string' ? first : first?.value) ?? '';
        } else if (typeof value === 'string') {
          update.warning = value;
        }
        return true;
      }
      return false;
    });
    const blockFields = selectBlockFieldNames(state, viewId);
    blockFields.some((fieldName) => {
      if (fieldName in data) {
        const value = data[fieldName];
        if (Array.isArray(value)) {
          const first = value[0];
          update.is_blocked = ['true', '1'].includes(
            String(
              typeof first === 'string' ? first : first?.value,
            ).toLowerCase(),
          );
        } else {
          update.is_blocked = Boolean(value);
        }
        return true;
      }
      return false;
    });
    const entityItems = Object.fromEntries(objectIds.map((id) => [id, update]));
    dispatch(
      updateEntities({
        search_items: entityItems,
        [objectType]: entityItems,
      }),
    );
  };

export function bulkMetadataValues(
  viewId: string,
  data: MetadataValues,
  objectIds: string[],
  objectType: SearchObjectType = OBJECT_TYPE_ASSETS,
) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    if (objectIds.length > 0) {
      return dispatch(
        httpPutByObjectTypeViewsByViewId(objectType, viewId, {
          metadata_values: formToServerFormat({}, pickBulkMarkedFields(data)),
          object_ids: objectIds,
        }),
      ).then(() => {
        dispatch(updateBlockWarningFields(viewId, data, objectIds, objectType));
      });
    }
    return Promise.resolve();
  };
}

export function putMetadataValues(
  objectId: string,
  viewId: string,
  data: MetadataValues,
  objectType: SearchObjectType = OBJECT_TYPE_ASSETS,
  version_id?: string,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    // set values to have the format the server expects.
    // do not update values that are the same as the previous
    const state = getState();
    const currentValues =
      state.metadataValuesReducer.metadataValues[objectId] ?? {};

    const currentSearchItem = state.entities.search_items[objectId] ?? {};

    const requestData = formToServerFormat(currentValues, data);

    if (Object.keys(requestData).length > 0) {
      return dispatch(
        httpPutByObjectTypeByObjectIdViewsByViewId(
          objectType,
          objectId,
          viewId,
          {
            version_id,
            metadata_values: requestData,
          },
          {
            ignore_unchanged: true,
          },
        ),
      ).then((response) => {
        dispatch(
          updateBlockWarningFields(viewId, data, [objectId], objectType),
        );

        const metadataUpdate = serverToReduxFormat(
          asFieldValues(response.body.metadata_values),
        );

        const formattedMetadataForSearchItemUpdate: Record<string, unknown> =
          {};

        Object.keys(metadataUpdate)?.forEach((fieldName) => {
          formattedMetadataForSearchItemUpdate[fieldName] = metadataUpdate[
            fieldName
          ]?.map((v) => v.value ?? v);
        });

        const entityItems = Object.fromEntries(
          [objectId].map((id) => [
            id,
            {
              metadata: {
                ...currentSearchItem.metadata,
                ...formattedMetadataForSearchItemUpdate,
              },
            },
          ]),
        );

        dispatch(
          updateEntities({
            search_items: entityItems,
            [objectType]: entityItems,
          }),
        );
        return dispatch(
          metadataValuesPutAsset(asMetadataValuesResponse(response.body)),
        );
      });
    }
    return Promise.resolve();
  };
}

export function putAssetSegmentMetadataValues(
  assetId: string,
  segmentId: string,
  viewId: string,
  data: MetadataValues,
) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    // set values to have the format the server expects.
    const requestData = formToServerFormat({}, data);
    if (Object.keys(requestData).length === 0) {
      return Promise.resolve();
    }
    return dispatch(
      httpPutAssetsByAssetIdByObjectTypeByObjectIdViewsByViewId(
        assetId,
        'segments',
        segmentId,
        viewId,
        {
          metadata_values: requestData,
        },
      ),
    );
  };
}

export function createAwaitingMetadataValues(
  assetId: string,
  values: MetadataFieldValuesPatched = {},
) {
  return (dispatch: AppDispatch, getState: GetState): void => {
    const metadataValues = getState().metadataValuesReducer.metadataValues;
    let currentViewValuesChanged = false;
    _forOwn(values, (value, key) => {
      // awaiting values are created if values are different and displayed
      if (
        metadataValues[assetId] &&
        !_isEqual(metadataValues[assetId][key], value.field_values)
      ) {
        currentViewValuesChanged = true;
      }
    });
    // fire action just if the updated values affect the metadata view that is visible
    if (metadataValues[assetId] && currentViewValuesChanged) {
      dispatch(
        metadataValuesCreateAwaiting({
          metadata_values: values,
          object_id: assetId,
        }),
      );
    }
  };
}

export function bulkUpdateMetadata(
  viewId: string,
  data: MetadataValues,
  objects: SearchItemEntity[],
) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    const requestPromises: Promise<unknown>[] = [];
    const objectsMap = _groupBy(objects, 'object_type');
    _forOwn(objectsMap, (items, type) => {
      // The grouping key is a search item's own object type, which lodash
      // widens to a plain string.
      if (!isSearchObjectType(type)) {
        return;
      }
      const ids = items.map((item) => item.id);
      requestPromises.push(
        dispatch(bulkMetadataValues(viewId, data, ids, type)),
      );
    });
    return Promise.all(requestPromises).then(() => {
      dispatch(
        successMessage({
          text: 'Metadata will be updated. This operation can take some time',
        }),
      );
    });
  };
}
